import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormsModule } from '@angular/forms';

// PrimeNG
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-custom-yes-no',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CheckboxModule,
  ],
  templateUrl: './custom-yes-no.component.html',
  styleUrls: ['./custom-yes-no.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomYesNoComponent),
      multi: true,
    },
  ],
})
export class CustomYesNoComponent implements ControlValueAccessor {
  @Input() disabled: boolean = false;
  @Input() fieldKey!: string;
  @Input() index: number | null = null;

  // Internal selected value: true (Yes), false (No), null (not selected)
  selectedValue: boolean | null = null;

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  // ControlValueAccessor methods
  writeValue(value: boolean | null): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Handle Yes checkbox change
  onYesChange(checked: boolean): void {
    if (this.disabled) return;

    if (checked) {
      this.selectedValue = true;
    } else {
      this.selectedValue = null;
    }

    this.onChangeFn(this.selectedValue);
    this.onTouchedFn();
  }

  // Handle No checkbox change
  onNoChange(checked: boolean): void {
    if (this.disabled) return;

    if (checked) {
      this.selectedValue = false;
    } else {
      this.selectedValue = null;
    }

    this.onChangeFn(this.selectedValue);
    this.onTouchedFn();
  }

  // Check if Yes is selected
  isYesSelected(): boolean {
    return this.selectedValue === true;
  }

  // Check if No is selected
  isNoSelected(): boolean {
    return this.selectedValue === false;
  }

  // Generate input IDs
  getYesInputId(): string {
    const indexPart = this.index !== null ? `_${this.index}` : '';
    return `${this.fieldKey}${indexPart}_yes`;
  }

  getNoInputId(): string {
    const indexPart = this.index !== null ? `_${this.index}` : '';
    return `${this.fieldKey}${indexPart}_no`;
  }
}
